import {
  campaignsApi,
  useGetAccountMembersQuery,
  useGetCampaignDetailsQuery,
  useGetCampaignSummaryQuery,
  useRemoveAccountPlaybooksMutation,
} from "api";
import { QUERY_SEPARATOR } from "constant";
import {
  useAppDispatch,
  useAppSelector,
  useDebounceValue,
  useHandleError,
  useStateWithSearchParams,
} from "hooks";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AccountPlanStatus,
  ColumnType,
  PlaybookPlanNames,
} from "../../../types/enums";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { InputWithQuery } from "../../ui/InputWithQuery";
import { DropdownWithQuery } from "../../ui/dropdown/DropdownWithQuery";
import { CustomCalendar } from "components/ui/calendar";
import { ResetAllFiltersButton } from "../../features/ResetAllFiltersButton";
import { OpenModalButton, ToTopBtn } from "../../ui/buttons";
import {
  AccountPlanCard,
  InfiniteScroll,
  NoContentBlock,
  ReuseModal,
  TableTabs,
} from "../../ui";
import { MemberPlaybookItem, IdType } from "types/apiTypes";
import { GeneratePlaybooksModal } from "../../ui/modal";
import { selectPermissions } from "../../../features/permissionsSlice";
import { CustomButton } from "../../ui/CustomButton";
import { toast } from "react-toastify";
import { CustomToast } from "../../ui/CustomToast";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { TrashIcon } from "../../images";
import { ExportPlaybooksBtn } from "../../ui/buttons/ExportPlaybooksBtn";

type SearchParams = {
  search?: string;
  date?: string;
  status_filter?: string;
};

const statusFilterList = ["All", "Ready", "Missing"];

const statusFilterMapper: Record<string, string | undefined> = {
  All: undefined,
  Ready: AccountPlanStatus.completed,
  "Needs Update": AccountPlanStatus.needs_update,
  Missing: AccountPlanStatus.ready,
};

const PER_PAGE = 25;

export const PlaybookReports = () => {
  const dispatch = useAppDispatch();

  const { campaign_id, account_id } = useParams();
  const { isEditor } = useAppSelector((state) => state.company.userRoles);
  const { isAllowedEditGenerateReports } = useAppSelector(selectPermissions);

  const [isConfirmModal, setIsConfirmModal] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [columnsType, setColumnsType] = useState<ColumnType>(ColumnType.grid);
  const [selectedPlaybooks, setSelectedPlaybooks] = useState<{
    [key: string]: boolean;
  }>({});

  // Мгновенный локальный статус карточек для bulk-операций (удаление),
  // пока не пришли актуальные данные с сервера после invalidateTags
  const [optimisticStatuses, setOptimisticStatuses] = useState<
    Record<string, AccountPlanStatus>
  >({});

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    search: undefined,
    date: undefined,
    status_filter: undefined,
  });

  const debounceSearch = useDebounceValue(searchDict.search);

  const [removeAccountPlaybooks, { isLoading: isRemoving }] =
    useRemoveAccountPlaybooksMutation();

  const { data, isFetching, isLoading, error, refetch } =
    useGetAccountMembersQuery(
      {
        account_id: account_id!,
        search: debounceSearch,
        page: page,
        per_page: PER_PAGE,
        status_filter: !isEditor
          ? AccountPlanStatus.completed
          : (statusFilterMapper[
              searchDict?.status_filter || "All"
            ] as AccountPlanStatus),
        start_date: searchDict.date?.split(QUERY_SEPARATOR)[0],
        end_date: searchDict.date?.split(QUERY_SEPARATOR)[1],
      },
      {
        skip: !account_id || isEditor === undefined,
        refetchOnMountOrArgChange: true,
      },
    );

  const { data: summary } = useGetCampaignSummaryQuery({
    campaign_id: campaign_id!,
  });

  const { data: details, refetch: refetchCampaignDetails } =
    useGetCampaignDetailsQuery({
      account_id: account_id!,
    });

  useHandleError(error);

  useEffect(() => {
    if (data) {
      setSelectedPlaybooks((prev) => ({
        ...prev,
        ...data.items.reduce(
          (acc, { member_id }) => ({ ...acc, [member_id]: false }),
          {},
        ),
      }));
    }
  }, [data]);

  // Как только пришли актуальные данные с сервера (после invalidateTags),
  // локальные оптимистичные статусы больше не нужны — реальные данные их заменят
  useEffect(() => {
    if (data) {
      setOptimisticStatuses((prev) => (Object.keys(prev).length ? {} : prev));
    }
  }, [data]);

  useEffect(() => {
    setPage(0);
  }, [debounceSearch, searchDict?.status_filter, searchDict?.date]);

  const isFilters = searchDict.date || searchDict?.status_filter;

  const isGrid = ColumnType.grid === columnsType;

  const canLoadMore = page + 1 < (data?.total_pages || 0);

  const transformData = useMemo(
    () =>
      (data?.items || []).map((el) => ({
        ...el,
        id: el.member_id,
      })),
    [data?.items],
  );

  const removePlaybooks = useMemo(
    () =>
      Object.entries(selectedPlaybooks).reduce((acc, [id, value]) => {
        if (value) return [...acc, id];
        return acc;
      }, [] as string[]),
    [selectedPlaybooks],
  );

  const selectHandler = (id: string) => {
    setSelectedPlaybooks((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const removePlaybooksHandler = () => {
    removeAccountPlaybooks({
      account_id: account_id!,
      campaign_id: campaign_id!,
      members_ids: removePlaybooks,
      include_all: false,
    })
      .unwrap()
      .then(() => {
        setOptimisticStatuses((prev) => {
          const next = { ...prev };
          removePlaybooks.forEach((id) => {
            next[id] = AccountPlanStatus.ready;
          });
          return next;
        });
        setSelectedPlaybooks({});
        setIsConfirmModal(false);
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title="Failed to Remove Playbooks."
          />,
        ),
      );
  };

  if (!details) return null;

  const { total_members, total_members_with_playbook } = details;

  return (
    <Stack gap="12px" flexGrow={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <TableTabs isGrid={isGrid} setColumnsType={setColumnsType} />
        <InputWithQuery />
        {isEditor && (
          <Stack>
            <DropdownWithQuery
              query={"status_filter"}
              label={""}
              items={statusFilterList}
              placeholder={""}
              initSelected={statusFilterList[0]}
              maxHeight="134px"
              maxItemsWidth={"172px"}
            />
          </Stack>
        )}

        <Stack flexDirection={"row"} gap="12px">
          <CustomCalendar date={searchDict.date} />
          {isFilters && <ResetAllFiltersButton />}
        </Stack>

        {summary && (
          <Stack
            direction="row"
            gap="12px"
            marginLeft="auto"
            alignItems="center"
          >
            {total_members_with_playbook > 0 && (
              <>
                <ExportPlaybooksBtn />
                {isAllowedEditGenerateReports && (
                  <CustomButton
                    onClick={() => setIsConfirmModal(true)}
                    variant="outlined"
                    customColor="#C0392B"
                    textColor="#FFFFFF"
                    customSize="fit"
                    type="button"
                    sx={{
                      borderColor: "#C0392B",
                      "&.Mui-disabled": {
                        backgroundColor: "#EEEEEE",
                        color: "#BCBCBC",
                        borderColor: "#EEEEEE",
                      },
                      height: "36px",
                    }}
                    disabled={removePlaybooks.length === 0 || isRemoving}
                    startIcon={
                      isRemoving ? (
                        <CircularProgress size={"24px"} color={"inherit"} />
                      ) : (
                        <TrashIcon
                          color={
                            removePlaybooks.length === 0 ? "#BCBCBC" : "#FFFFFF"
                          }
                        />
                      )
                    }
                  >
                    Delete
                    {removePlaybooks.length > 0
                      ? ` (${removePlaybooks.length})`
                      : ""}
                  </CustomButton>
                )}
              </>
            )}

            {total_members_with_playbook !== total_members &&
              isAllowedEditGenerateReports && (
                <OpenModalButton
                  isDisabled={!isAllowedEditGenerateReports}
                  isGradient={true}
                  btnText={`Generate ${PlaybookPlanNames.playbooks}`}
                  renderModal={({ isOpen, onClose, setIsLoading }) => (
                    <GeneratePlaybooksModal
                      isOpen={isOpen}
                      onClose={onClose}
                      setIsLoading={setIsLoading}
                    />
                  )}
                />
              )}
          </Stack>
        )}
      </Box>
      <InfiniteScroll<MemberPlaybookItem & IdType>
        hasMore={canLoadMore}
        isLoading={isFetching}
        loadMore={() => setPage((prev) => prev + 1)}
        page={page}
        items={transformData}
        scrollOffset={1200}
        showScrollTopOffset={100}
        startPage={0}
      >
        {({ showScrollTop, items }) => (
          <>
            {isLoading ? (
              <Box
                display="flex"
                height="25vh"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress size={80} sx={{ color: "#FE8836" }} />
              </Box>
            ) : (
              <>
                {items.length === 0 &&
                !isFetching &&
                (isFilters || !!searchDict.search) ? (
                  <Stack
                    gap="8px"
                    alignItems="center"
                    justifyContent="center"
                    padding="32px"
                  >
                    <NoContentBlock
                      title={`No Matching ${PlaybookPlanNames.playbooks}`}
                      text={
                        "No playbooks match your search. Please try another keyword."
                      }
                    />
                  </Stack>
                ) : (
                  <Stack
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: `repeat(${isGrid ? 2 : 1}, 1fr)`,
                        lg: `repeat(${isGrid ? 3 : 1}, 1fr)`,
                      },
                      gap: "12px",
                    }}
                  >
                    {items.map(
                      ({
                        member_id,
                        member_name,
                        updated_at,
                        status,
                        is_new,
                      }) => (
                        <AccountPlanCard
                          key={member_id}
                          id={member_id}
                          campaign_id={campaign_id!}
                          name={member_name}
                          updatedAt={updated_at}
                          isGrid={isGrid}
                          isNeedsUpdate={false}
                          status={status}
                          statusOverride={optimisticStatuses[member_id]}
                          isNew={is_new}
                          isPlaybook={true}
                          isEditor={isEditor}
                          isSelectMode={!!isAllowedEditGenerateReports}
                          isSelected={selectedPlaybooks[member_id]}
                          setSelected={selectHandler}
                          account_id={account_id}
                        />
                      ),
                    )}

                    {showScrollTop && <ToTopBtn />}
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </InfiniteScroll>
      {isFetching && page !== 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={"8vh"}
        >
          <CircularProgress size={30} sx={{ color: "#FE8836" }} />
        </Box>
      )}

      {isConfirmModal && (
        <ReuseModal
          isOpen={isConfirmModal}
          onClose={() => setIsConfirmModal(false)}
          title="Delete Playbooks"
          maxWidth="430px"
          submitBtnText="Delete"
          submitBtnColor="#C0392B"
          onSubmit={removePlaybooksHandler}
          isLoading={isRemoving}
          isDisabled={isRemoving}
          isDisabledCancelBtn={isRemoving}
        >
          <Stack gap={"12px"} paddingBottom={"16px"} paddingTop={"8px"}>
            <Typography
              component="span"
              variant="subtitle1"
              sx={{ color: "#464646" }}
            >
              Are you sure you want to delete ({removePlaybooks.length})
              Playbooks?
            </Typography>
            <Typography
              component="span"
              variant="subtitle1"
              sx={{ color: "#464646" }}
            >
              You’ll need to generate it again if you need it later.
            </Typography>
          </Stack>
        </ReuseModal>
      )}
    </Stack>
  );
};
